import { createAppAsyncThunk } from '../store/extensions';
import { ensureSignalRConnected } from '../app/signalRConnection';
import {
  AI_CHATBOT_BEGIN_RESPONSE,
  AI_CHATBOT_CANCEL_RESPONSE,
} from './actionTypes';
import { FetchAIPromptsRequestDTO } from './dtos/FetchAIPromptsRequestDTO';
import { SendAIChatbotMessageRequestDTO } from './dtos/SendAIChatbotMessageRequestDTO';
import { getLegacyTargetsForChatbotRoute } from './htmlSnapshots';
import { createAIResponseId } from './responseId';
import { selectMember } from '../profile/selectors';
import { nativeSurveyBlocksComposer } from './interactiveSurvey/recoveryDisplay';
import {
  expectNativeSurvey,
  failPendingNativeSurveyRequest,
  getNativeSurveyState,
  INITIAL_REQUEST_TTL_MS,
  NativeSurveyRequestContext,
  registerPendingNativeSurveyRequest,
  resolvePendingNativeSurveyRequest,
} from './interactiveSurvey/store';
import { cancelAIChatbotResponseCall, fetchAIPromptsCall, sendAIChatbotMessageCall } from './service';
import { setAIResponseBackendCancellationStatus, setAIResponseCancellationSupport, setConversationSessionId, stopAIResponse } from './slice';

export const stopAIResponseStreaming = createAppAsyncThunk(
  'aiChatbot/stopStreaming',
  async (responseId: string, thunkAPI) => {
    const state = thunkAPI.getState().aiChatbot;
    const response = state.htmlSnapshotResponses[responseId];
    if (!response || response.isComplete || response.agentId !== state.activeChatbotVariant
      || state.activeResponseByLegacyTarget[response.legacyTarget] !== responseId) return;
    const survey = getNativeSurveyState();
    if (response.agentId === 'maya' && survey.binding?.sessionId === response.sessionId
      && nativeSurveyBlocksComposer(survey)) return;
    thunkAPI.dispatch(stopAIResponse(responseId));
    if (!response.supportsRequestCancellation || response.backendCancellationStatus === 'pending'
      || response.backendCancellationStatus === 'accepted') return;
    thunkAPI.dispatch(setAIResponseBackendCancellationStatus({ responseId, status: 'pending' }));
    try {
      const result = await cancelAIChatbotResponseCall(responseId, state.activeChatbotVariant);
      if (result.data?.version !== 1 || result.data?.responseId !== responseId
        || !['cancellation_requested', 'completed'].includes(result.data?.status))
        throw new Error('invalid_chat_cancellation_response');
      thunkAPI.dispatch(setAIResponseBackendCancellationStatus({ responseId, status: 'accepted' }));
    } catch {
      thunkAPI.dispatch(setAIResponseBackendCancellationStatus({ responseId, status: 'failed' }));
    }
  }
);

export const sendAIChatbotMessage = createAppAsyncThunk(
  'aiChatbot/sendMessage',
  async (
    arg:
      | SendAIChatbotMessageRequestDTO
      | { request: SendAIChatbotMessageRequestDTO; clientAction?: string },
    thunkAPI
  ) => {
    const request = 'request' in arg ? arg.request : arg;
    const clientAction = 'request' in arg ? arg.clientAction : undefined;
    const responseId = createAIResponseId();
    const native = getNativeSurveyState();
    if ((!request.chatbotRoute || request.chatbotRoute === 'mcpmaya') &&
        native.binding?.sessionId === request.sessionId && native.binding?.roomId === request.roomId &&
        nativeSurveyBlocksComposer(native)) {
      throw new Error('survey_action_required');
    }
    const legacyTargets = getLegacyTargetsForChatbotRoute(request.chatbotRoute);
    const currentState = thunkAPI.getState();
    const isMaya = !request.chatbotRoute || request.chatbotRoute === 'mcpmaya';
    const memberId = isMaya && !request.sessionId && request.roomId
      ? selectMember(currentState)?.id
      : undefined;
    const pendingSurveyContext: NativeSurveyRequestContext | undefined =
      isMaya && request.interactiveSurveySchemaVersion === 1 && !request.sessionId && request.roomId && memberId &&
      currentState.aiChatbot.activeChatbotVariant === 'maya'
        ? { accountId: memberId, agentId: 'maya', roomId: request.roomId }
        : undefined;

    const connected = await ensureSignalRConnected();
    if (!connected) {
      throw new Error('SignalR must be connected before submitting AI chat.');
    }

    const activeResponses =
      thunkAPI.getState()?.aiChatbot?.activeResponseByLegacyTarget ?? {};
    const busyTarget = legacyTargets.find((target) => activeResponses[target]);
    if (busyTarget) {
      throw new Error(
        `An AI response is already active for legacy target ${busyTarget}.`
      );
    }

    thunkAPI.dispatch({
      type: AI_CHATBOT_BEGIN_RESPONSE,
      payload: {
        responseId,
        legacyTargets,
        sessionId: request.sessionId,
      },
    });

    try {
      if (pendingSurveyContext) {
        registerPendingNativeSurveyRequest(pendingSurveyContext, responseId);
      } else if (isMaya) {
        expectNativeSurvey(request.sessionId, request.roomId, responseId);
      }
      let initialRequestTimer: ReturnType<typeof setTimeout> | undefined;
      const requestCall = sendAIChatbotMessageCall(request, clientAction, responseId);
      const response = pendingSurveyContext
        ? await Promise.race([
            requestCall,
            new Promise<never>((_, reject) => {
              initialRequestTimer = setTimeout(
                () => reject(new Error('survey_initialization_timeout')),
                INITIAL_REQUEST_TTL_MS
              );
            }),
          ]).finally(() => clearTimeout(initialRequestTimer))
        : await requestCall;
      const returnedSessionId = response.data?.sessionId?.trim();
      const boundSessionId =
        thunkAPI.getState()?.aiChatbot?.htmlSnapshotResponses?.[responseId]
          ?.sessionId;
      if (
        returnedSessionId &&
        boundSessionId &&
        returnedSessionId !== boundSessionId
      ) {
        throw new Error('chat_response_scope_mismatch');
      }
      if (pendingSurveyContext) {
        if (!returnedSessionId) {
          throw new Error('survey_initialization_failed');
        }
        const previousSessionId = thunkAPI.getState()?.aiChatbot?.conversationSessionId;
        thunkAPI.dispatch(setConversationSessionId(returnedSessionId));
        if (!resolvePendingNativeSurveyRequest(
          pendingSurveyContext,
          responseId,
          returnedSessionId
        )) {
          thunkAPI.dispatch(setConversationSessionId(previousSessionId));
          throw new Error('survey_initialization_failed');
        }
      }

      if (response.data?.supportsRequestCancellation === true && returnedSessionId) {
        thunkAPI.dispatch(setAIResponseCancellationSupport({ responseId, sessionId: returnedSessionId }));
        if (thunkAPI.getState().aiChatbot.htmlSnapshotResponses[responseId]?.streamingStopped)
          thunkAPI.dispatch(stopAIResponseStreaming(responseId));
      }
      return response;
    } catch (error) {
      failPendingNativeSurveyRequest(responseId);
      thunkAPI.dispatch({
        type: AI_CHATBOT_CANCEL_RESPONSE,
        payload: responseId,
      });
      throw error;
    }
  }
);

export const fetchAIPrompts = createAppAsyncThunk(
  'aiChatbot/fetchPrompts',
  (
    arg:
      | FetchAIPromptsRequestDTO
      | { request: FetchAIPromptsRequestDTO; clientAction?: string }
  ) => {
    const request = 'request' in arg ? arg.request : arg;
    const clientAction = 'request' in arg ? arg.clientAction : undefined;
    return fetchAIPromptsCall(request, clientAction);
  }
);
